'use client';

import { Grid } from '@sbercloud/uikit-product-site-grid';
import { SectionBasic } from '@sbercloud/uikit-product-site-section';
import {
  Skeleton,
  SkeletonContextProvider,
  WithSkeleton,
} from '@snack-uikit/skeleton';
import { useRouter } from 'next/navigation';
import { memo, MouseEvent, useEffect, useState } from 'react';
import { CasesItem } from 'src/components/CasesCards';
import { useSearchParams } from 'src/contexts/SearchParams/useSearchParams';
import { backendHost } from 'src/global';
import { useAnalytics } from 'src/hooks/useAnalytics';
import { useAsPath } from 'src/hooks/useAsPath';
import { useLayoutType } from 'src/hooks/useLayoutType';
import { Layout } from 'src/layout';
import { Case, CaseCategory, ResponseCases } from 'src/types/backendContent';
import { HeroInnerPage } from 'src/uikit/HeroInnerPage';

import { LIMIT_CASE_CARDS } from './limitCaseCards';

export type CasesDistributionPageType = {
  cases: Case[];
  categories: CaseCategory[];
  maxPages: number;
  currentPage: number;
  mappedCases: MappedCases;
};

export type MappedCases = {
  [slug: string]: Case[];
};

const MockCases = {
  spoilerTitle: 'Отрасль',
  header: {
    title: 'Успешные кейсы клиентов',
    description:
      'Компании, которые создают и развивают свой бизнес на решениях Cloud.ru.',
    breadcrumbs: [
      { title: 'Главная', link: '/' },
      { title: 'Кейсы', link: '' },
    ],
  },
  buttonTitle: 'Показать ещё',
};

const CasesCards = memo(function CasesCards({
  cases,
  loading,
  countShowCases,
  curPage,
  variant,
}: {
  cases: Case[];
  loading: boolean;
  countShowCases: number;
  curPage: number;
  variant: PaginationVariantEnum;
}) {
  const leftIndex =
    countShowCases === LIMIT_CASE_CARDS
      ? (curPage - 1) * LIMIT_CASE_CARDS
      : (curPage - countShowCases / LIMIT_CASE_CARDS) * LIMIT_CASE_CARDS;
  const rightIndex = leftIndex + countShowCases;

  const layoutType = useLayoutType();

  return (
    <Grid
      columnsConfig={{
        desktop: { amount: 2, minWidth: 300 },
        tablet: { amount: 1, minWidth: 220 },
        mobile: { amount: 1, minWidth: 180 },
      }}
      layoutType={layoutType}
    >
      {variant === PaginationVariantEnum.default &&
        cases?.map(({ categories, slug, title, image, id, ...rest }) => (
          <CasesItem
            key={id}
            link={slug}
            pagePath="cases"
            categories={categories}
            image={image}
            title={title}
            rest={rest}
          />
        ))}

      {variant === PaginationVariantEnum.withFilter &&
        cases
          ?.slice(leftIndex, rightIndex)
          .map(({ categories, slug, title, image, id, ...rest }) => (
            <CasesItem
              key={id}
              link={slug}
              pagePath="cases"
              categories={categories}
              image={image}
              title={title}
              rest={rest}
            />
          ))}
      <SkeletonContextProvider loading={loading}>
        <WithSkeleton skeleton={<SkeletonCards />} />
      </SkeletonContextProvider>
    </Grid>
  );
});

function SkeletonCards() {
  return Array(6)
    .fill(0)
    .map((_, index) => <Skeleton key={index} height={216} />);
}

enum PaginationVariantEnum {
  default = 'default',
  withFilter = 'withFilter',
}

export function CasesDistributionPage({
  categories,
  mappedCases,
  cases,
  currentPage,
  maxPages,
}: CasesDistributionPageType) {
  const [paginationVariant, setPaginationVariant] = useState(
    PaginationVariantEnum.default,
  );
  const [curCategories] = useState(categories);
  const [activeCategory, setActiveCategory] = useState(categories[0]);
  const [curMaxPages, setCurMaxPages] = useState(maxPages);
  const [countShowCases, setCountShowCases] = useState(LIMIT_CASE_CARDS);

  const [casesByCategories] = useState(mappedCases);
  const [curCases, setCurCases] = useState(cases);
  const [curPage, setCurPage] = useState(currentPage);
  const [loading, setLoading] = useState(false);

  const { clickAnalytics } = useAnalytics();
  const searchParams = useSearchParams();
  const router = useRouter();

  useEffect(() => {
    const industryParameter = searchParams.get('industry');
    if (industryParameter) {
      const foundedCategory = curCategories.find(
        (category) => category.slug === industryParameter,
      );
      if (foundedCategory !== activeCategory)
        foundedCategory && setActiveCategory(foundedCategory);
    }
  }, [searchParams]);

  useEffect(() => {
    if (activeCategory.slug === 'all') {
      setPaginationVariant(PaginationVariantEnum.default);
      setCurCases(cases);
      setCurMaxPages(maxPages);
    } else {
      setPaginationVariant(PaginationVariantEnum.withFilter);
      setCurCases(casesByCategories[activeCategory.slug]);
      setCurMaxPages(
        Math.floor(
          (casesByCategories[activeCategory.slug].length - 1) /
            LIMIT_CASE_CARDS,
        ) + 1,
      );
    }

    setCountShowCases(LIMIT_CASE_CARDS);
  }, [activeCategory.slug]);

  useEffect(() => {
    setCurCases(cases);
  }, [cases]);

  const handleShowMore = async () => {
    const fetchMore = async () => {
      setLoading(true);

      fetch(
        `${backendHost}/content/v1/client_cases?page=${
          curPage + 1
        }&limit=${LIMIT_CASE_CARDS}`,
      )
        .then((res) => {
          if (!res.ok) {
            throw Error(res.statusText);
          }
          return res.json();
        })
        .then((res: ResponseCases) => {
          setCurCases((prev) => [...prev, ...res.data.clientCases]);
          globalThis?.history.pushState(null, '', `/cases/${curPage + 1}`);
          setCurPage((prev) => prev + 1);
        })
        .catch((e) => {
          // eslint-disable-next-line no-console
          console.error(`Failed to fetch cases: ${e.message}`);
        })
        .finally(() => setLoading(false));
    };

    clickAnalytics({
      action: 'click',
      clickZone: 'body',
      clickElement: 'button',
      clickContent: MockCases.buttonTitle,
      uniqueId: 'blog-button-show-more',
      transitionType: 'inside-link',
    });

    if (paginationVariant === PaginationVariantEnum.default) {
      await fetchMore();
    } else {
      setCurPage((prev) => prev + 1);
      setCountShowCases((prev) => prev + LIMIT_CASE_CARDS);
    }
  };

  const handleChangePage = (
    page: number,
    event?: MouseEvent<HTMLAnchorElement>,
  ) => {
    event?.preventDefault();

    const changeRoute = () => {
      if (page === curPage) return;
      const rout = page === 1 ? `/cases` : `/cases/${page}`;

      clickAnalytics({
        action: rout,
        clickZone: 'body',
        clickElement: 'button',
        clickContent: page.toString(),
        uniqueId: `blog-pagination-button-${page}`,
        transitionType: 'inside-link',
      });

      const casesParam = searchParams.get('cases');

      if (!casesParam && page === 1) {
        window.location.pathname = '/cases';
      }

      if (casesParam === page.toString()) {
        window.location.pathname = `/cases/${casesParam}`;
      }

      router.push(rout);
    };

    setCurPage(page);
    setCountShowCases(LIMIT_CASE_CARDS);
    if (paginationVariant === PaginationVariantEnum.default) changeRoute();
  };

  const hrefFormatter = (page: number) =>
    page === 1 ? '/cases' : `/cases/${page}`;

  const layoutType = useLayoutType();

  const asPath = useAsPath();

  return (
    <Layout>
      <HeroInnerPage
        title={MockCases.header.title}
        description={MockCases.header.description}
        breadcrumbs={{ links: MockCases.header.breadcrumbs }}
      />

      <SectionBasic
        layoutType={layoutType}
        filterItems={[
          {
            id: 'cases',
            options: curCategories.map((category) => ({
              option: category.title,
              value: category.id,
              'data-test-id': `spoiler-click-${category.title}`,
            })),
            onChange: (item) => {
              const category = curCategories.find(
                (category) => category.id === item,
              );

              if (category) {
                setActiveCategory(category);

                clickAnalytics({
                  action: '/',
                  clickZone: 'body',
                  clickElement: 'button',
                  clickContent: category.title,
                  uniqueId: `spoiler-click-${category.title}`,
                  transitionType: 'inside-link',
                });
                if (!isNaN(Number(asPath.split('/').at(-1)))) {
                  router.push(`/cases?industry=${category.slug}`);
                } else {
                  globalThis?.history.pushState(
                    null,
                    '',
                    `/cases?industry=${category.slug}`,
                  );
                  setActiveCategory(category);
                }

                setCurPage(1);
              }
            },
            searchable: false,
            value: activeCategory.id,
            showClearButton: false,
          },
        ]}
        pagination={
          curMaxPages > 1
            ? {
                page: curPage,
                total: curMaxPages,
                onChange: handleChangePage,
                hrefFormatter: hrefFormatter,
                variant: 'link',
              }
            : undefined
        }
        moreButton={
          curPage < curMaxPages
            ? {
                label: MockCases.buttonTitle,
                'data-test-id': 'show-more-btn',
                onClick: () => {
                  handleShowMore();
                  clickAnalytics({
                    action: 'click',
                    clickZone: 'body',
                    clickElement: 'button',
                    clickContent: MockCases.buttonTitle,
                    uniqueId: `cases-show-more-button-${MockCases.buttonTitle}`,
                    transitionType: 'inside-link',
                  });
                },
              }
            : undefined
        }
      >
        <div id="content">
          <CasesCards
            cases={curCases}
            loading={loading}
            countShowCases={countShowCases}
            curPage={curPage}
            variant={paginationVariant}
          />
        </div>
      </SectionBasic>
    </Layout>
  );
}

export function CasesPage(data: CasesDistributionPageType) {
  return <CasesDistributionPage {...data} />;
}
